import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Loader2, AlertTriangle, MessageSquare, ShieldCheck, CheckCircle, XCircle, Building2, Package } from 'lucide-react';
import { adminListDisputes, adminResolveDispute, addDisputeMessage } from '@/lib/api';
import { toast } from 'sonner';
import { fmt2 } from '@/lib/utils';

const STATUS_CFG = {
  open: { label: 'Offen', color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400' },
  under_review: { label: 'In Prüfung', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' },
  resolved_refund_full: { label: 'Vollerstattung', color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
  resolved_refund_partial: { label: 'Teilerstattung', color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400' },
  resolved_rejected: { label: 'Abgelehnt', color: 'bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300' },
};

const DISPUTE_TYPE_LABEL = {
  damage: 'Beschädigt',
  missing: 'Fehlt',
  wrong_item: 'Falscher Artikel',
  not_received: 'Nicht erhalten',
  quality: 'Qualität',
  other: 'Sonstiges',
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

const ResolveDialog = ({ open, onOpenChange, dispute, onSuccess }) => {
  const [resolution, setResolution] = useState('refund_full');
  const [refundAmount, setRefundAmount] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && dispute) {
      setResolution('refund_full');
      setRefundAmount(String(dispute.transaction?.payment_total_EUR || ''));
      setAdminNotes('');
    }
  }, [open, dispute]);

  if (!dispute) return null;
  const paymentTotal = dispute.transaction?.payment_total_EUR || 0;

  const handleSubmit = async () => {
    if (adminNotes.trim().length < 3) {
      toast.error('Bitte Admin-Begründung angeben (mind. 3 Zeichen)');
      return;
    }
    if (resolution === 'refund_partial') {
      const amt = parseFloat(refundAmount);
      if (!amt || amt <= 0) {
        toast.error('Bitte gültigen Erstattungsbetrag angeben');
        return;
      }
      if (amt > paymentTotal) {
        toast.error(`Betrag darf maximal ${fmt2(paymentTotal)} EUR sein`);
        return;
      }
    }
    setSubmitting(true);
    try {
      const payload = {
        resolution,
        admin_notes: adminNotes.trim(),
      };
      if (resolution === 'refund_partial') {
        payload.refund_amount_eur = parseFloat(refundAmount);
      }
      await adminResolveDispute(dispute.dispute_id, payload);
      toast.success('Reklamation entschieden');
      onSuccess?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Entscheidung fehlgeschlagen');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="resolve-dispute-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-purple-500" />
            Reklamation entscheiden
          </DialogTitle>
          <DialogDescription>
            Wählen Sie die Entscheidung. Bei Erstattung wird die Zahlung über Stripe zurückgebucht.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="bg-muted/40 rounded p-3 text-sm">
            <p className="font-medium">{dispute.material?.common_title || 'Artikel'}</p>
            <p className="text-xs text-muted-foreground">Bestell-Nr.: {dispute.transaction?.order_number}</p>
            <p className="text-xs text-muted-foreground">Zahlbetrag: <span className="font-medium text-foreground">{fmt2(paymentTotal)} EUR</span></p>
          </div>

          <div>
            <Label className="text-sm mb-2 block">Entscheidung</Label>
            <RadioGroup value={resolution} onValueChange={setResolution}>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="refund_full" id="r-full" data-testid="resolve-full" />
                <Label htmlFor="r-full" className="text-sm cursor-pointer">Vollerstattung an Käufer</Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="refund_partial" id="r-partial" data-testid="resolve-partial" />
                <Label htmlFor="r-partial" className="text-sm cursor-pointer">Teilerstattung</Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="reject" id="r-reject" data-testid="resolve-reject" />
                <Label htmlFor="r-reject" className="text-sm cursor-pointer">Reklamation ablehnen (zugunsten Verkäufer)</Label>
              </div>
            </RadioGroup>
          </div>

          {resolution === 'refund_partial' && (
            <div>
              <Label className="text-sm">Erstattungsbetrag (EUR) *</Label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                max={paymentTotal}
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
                placeholder={String(paymentTotal)}
                className="mt-1"
                data-testid="resolve-amount-input"
              />
              <p className="text-[11px] text-muted-foreground mt-1">Max. {fmt2(paymentTotal)} EUR</p>
            </div>
          )}

          <div>
            <Label className="text-sm">Begründung *</Label>
            <Textarea
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              rows={3}
              placeholder="Interne Notiz + Begründung für Käufer/Verkäufer …"
              className="mt-1"
              data-testid="resolve-notes-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button
            className="bg-purple-500 hover:bg-purple-600"
            onClick={handleSubmit}
            disabled={submitting}
            data-testid="resolve-confirm-btn"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <ShieldCheck className="h-4 w-4 mr-1" />}
            Entscheidung bestätigen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const DisputeDetailDialog = ({ open, onOpenChange, dispute, onChange }) => {
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) setMessage('');
  }, [open]);

  if (!dispute) return null;
  const cfg = STATUS_CFG[dispute.status];
  const isResolved = dispute.status?.startsWith('resolved_');

  const handleSend = async () => {
    if (message.trim().length < 1) return;
    setSending(true);
    try {
      const res = await addDisputeMessage(dispute.dispute_id, message.trim());
      setMessage('');
      onChange?.(res.data);
      toast.success('Nachricht gesendet');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Senden fehlgeschlagen');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="admin-dispute-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Reklamation
            <Badge className={cfg?.color}>{cfg?.label}</Badge>
          </DialogTitle>
          <DialogDescription>
            <span className="font-mono text-sm">{dispute.transaction?.order_number || dispute.dispute_id.slice(0, 8)}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 bg-muted/40 rounded-lg p-3 text-sm">
            <div>
              <p className="text-xs text-muted-foreground flex items-center gap-1"><Package className="h-3 w-3" /> Artikel</p>
              <p className="font-medium">{dispute.material?.common_title || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Reklamationstyp</p>
              <p className="font-medium">{DISPUTE_TYPE_LABEL[dispute.dispute_type] || dispute.dispute_type}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground flex items-center gap-1"><Building2 className="h-3 w-3" /> Käufer</p>
              <p className="font-medium">{dispute.buyer_group?.group_name || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground flex items-center gap-1"><Building2 className="h-3 w-3" /> Verkäufer</p>
              <p className="font-medium">{dispute.seller_group?.group_name || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Zahlbetrag</p>
              <p className="font-medium">{fmt2(dispute.transaction?.payment_total_EUR || 0)} EUR</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Eröffnet am</p>
              <p className="font-medium">{formatDate(dispute.created_at)}</p>
            </div>
          </div>

          {dispute.evidence_urls?.length > 0 && (
            <div>
              <p className="text-xs text-muted-foreground mb-1">Belege</p>
              <div className="flex flex-wrap gap-2">
                {dispute.evidence_urls.map((url, i) => (
                  <a key={i} href={url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline bg-blue-50 dark:bg-blue-900/20 px-2 py-1 rounded">
                    Beleg #{i + 1}
                  </a>
                ))}
              </div>
            </div>
          )}

          <div className="border rounded-lg divide-y max-h-[40vh] overflow-y-auto">
            {(dispute.messages || []).map((msg, idx) => (
              <div key={idx} className={`p-3 ${msg.role === 'admin' ? 'bg-purple-50 dark:bg-purple-900/10' : ''}`}>
                <div className="flex items-center gap-2 mb-1 text-xs">
                  <span className="font-semibold capitalize">{msg.role}</span>
                  <span className="text-muted-foreground">· {formatDate(msg.at)}</span>
                </div>
                <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
              </div>
            ))}
          </div>

          {!isResolved && (
            <div>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={2}
                placeholder="Antwort als Admin …"
                data-testid="admin-dispute-msg-input"
              />
              <div className="flex justify-end mt-2">
                <Button size="sm" onClick={handleSend} disabled={sending || message.trim().length < 1} data-testid="admin-dispute-msg-send-btn">
                  {sending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <MessageSquare className="h-4 w-4 mr-1" />}
                  Senden
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const AdminDisputesTab = () => {
  const [tab, setTab] = useState('open');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailDialog, setDetailDialog] = useState({ open: false, dispute: null });
  const [resolveDialog, setResolveDialog] = useState({ open: false, dispute: null });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminListDisputes(tab === 'all' ? undefined : tab);
      setItems(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reklamationen konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const openCounts = items.filter((d) => ['open', 'under_review'].includes(d.status)).length;

  return (
    <Card data-testid="admin-disputes-tab">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Reklamationen (Disputes)
          </h2>
          <Button size="sm" variant="outline" onClick={load} data-testid="refresh-disputes-btn">
            Aktualisieren
          </Button>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="space-y-4">
          <TabsList>
            <TabsTrigger value="open" data-testid="disputes-tab-open">
              Offen
              {openCounts > 0 && tab === 'open' && (
                <Badge className="ml-2 bg-amber-500">{openCounts}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="resolved" data-testid="disputes-tab-resolved">Gelöst</TabsTrigger>
            <TabsTrigger value="all" data-testid="disputes-tab-all">Alle</TabsTrigger>
          </TabsList>

          <TabsContent value={tab}>
            {loading ? (
              <div className="flex justify-center py-10">
                <Loader2 className="h-6 w-6 animate-spin text-amber-500" />
              </div>
            ) : items.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">Keine Reklamationen.</p>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 dark:bg-slate-800">
                      <tr>
                        <th className="text-left px-3 py-2 font-medium">Bestell-Nr.</th>
                        <th className="text-left px-3 py-2 font-medium">Eröffnet</th>
                        <th className="text-left px-3 py-2 font-medium">Artikel</th>
                        <th className="text-left px-3 py-2 font-medium">Käufer</th>
                        <th className="text-left px-3 py-2 font-medium">Verkäufer</th>
                        <th className="text-left px-3 py-2 font-medium">Typ</th>
                        <th className="text-right px-3 py-2 font-medium">Betrag</th>
                        <th className="text-center px-3 py-2 font-medium">Status</th>
                        <th className="text-center px-3 py-2 font-medium">Aktionen</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {items.map((d) => {
                        const cfg = STATUS_CFG[d.status];
                        const canResolve = ['open', 'under_review'].includes(d.status);
                        return (
                          <tr key={d.dispute_id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40" data-testid={`dispute-row-${d.dispute_id}`}>
                            <td className="px-3 py-2 font-mono text-xs font-semibold">{d.transaction?.order_number || d.transaction_id?.slice(0, 8)}</td>
                            <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{formatDate(d.created_at)}</td>
                            <td className="px-3 py-2 max-w-[180px] truncate">{d.material?.common_title || '—'}</td>
                            <td className="px-3 py-2 max-w-[140px] truncate">{d.buyer_group?.group_name || '—'}</td>
                            <td className="px-3 py-2 max-w-[140px] truncate">{d.seller_group?.group_name || '—'}</td>
                            <td className="px-3 py-2">{DISPUTE_TYPE_LABEL[d.dispute_type] || d.dispute_type}</td>
                            <td className="px-3 py-2 text-right font-medium">{fmt2(d.transaction?.payment_total_EUR || 0)} EUR</td>
                            <td className="px-3 py-2 text-center">
                              <Badge className={cfg?.color}>{cfg?.label}</Badge>
                            </td>
                            <td className="px-3 py-2 text-center">
                              <div className="flex justify-center gap-1">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 px-2 text-xs"
                                  onClick={() => setDetailDialog({ open: true, dispute: d })}
                                  data-testid={`view-dispute-${d.dispute_id}`}
                                >
                                  <MessageSquare className="h-3 w-3 mr-1" /> Details
                                </Button>
                                {canResolve && (
                                  <Button
                                    size="sm"
                                    className="h-7 px-2 bg-purple-500 hover:bg-purple-600 text-xs"
                                    onClick={() => setResolveDialog({ open: true, dispute: d })}
                                    data-testid={`resolve-dispute-${d.dispute_id}`}
                                  >
                                    <ShieldCheck className="h-3 w-3 mr-1" /> Entscheiden
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>

        <DisputeDetailDialog
          open={detailDialog.open}
          onOpenChange={(open) => setDetailDialog({ open, dispute: open ? detailDialog.dispute : null })}
          dispute={detailDialog.dispute}
          onChange={(updated) => {
            setDetailDialog((prev) => ({ ...prev, dispute: updated }));
            setItems((prev) => prev.map((it) => it.dispute_id === updated.dispute_id ? updated : it));
          }}
        />
        <ResolveDialog
          open={resolveDialog.open}
          onOpenChange={(open) => setResolveDialog({ open, dispute: open ? resolveDialog.dispute : null })}
          dispute={resolveDialog.dispute}
          onSuccess={load}
        />
      </CardContent>
    </Card>
  );
};
